import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { joinMeeting, leaveMeeting } from '@/redux/reducers/consultation.reducer'
import { isStaff } from '@/utils/type-guards'

const MEETING_TYPE = 'visioConference'

/**
 * Joins the visio-conference meeting for the connected staff on mount and leaves it on unmount.
 * The room is resolved, in order, from the `consultationId` query param, the `roomId` query param
 * (doctors only) or the PDS's emergency room. Returns the join handler for the meeting UI's
 * reconnect action.
 */
export function useVisioConferenceMeeting() {
  const [searchParams] = useSearchParams()
  const consultationId = searchParams.get('consultationId')
  const roomId = searchParams.get('roomId')
  const dispatch = useAppDispatch()
  const authUser = useAppSelector(selectAuthUser)

  const join = useCallback(() => {
    if (!isStaff(authUser)) return

    const userId = authUser._id
    const emergencyRoomId = typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom?._id

    const roomOrConsultationId = consultationId ?? (authUser.status === 'MEDECIN' ? roomId : null) ?? (authUser.status === 'PDS' ? emergencyRoomId : null)
    if (!roomOrConsultationId) return

    dispatch(joinMeeting({ roomOrConsultationId, userId, meetingType: MEETING_TYPE }))
  }, [authUser, roomId, consultationId, dispatch])

  useEffect(() => {
    join()
    return () => {
      dispatch(leaveMeeting(MEETING_TYPE))
    }
  }, [join, dispatch])

  return { join }
}
